import './DropdownToggle.scss';
import cn from 'classnames';
import { KeyboardEvent, MouseEvent, ReactElement, ReactNode, Ref, useMemo } from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { Placement, PlacementType } from '../designConstants';
import { useToolbarSize } from '../Toolbar';
import CaretIcon from './CaretIcon.svg?react';
import {
  DropdownOption,
  DropdownSharedProps,
  DropdownToggleType,
  DropdownToggleTypeValue,
  typeAppearance
} from './dropdown-constants';
import { DropdownToggleShell } from './DropdownToggleShell';

// Types whose string value is applied as a CSS class on the toggle button.
const NAMED_TYPES = new Set<DropdownToggleTypeValue>([
  DropdownToggleType.WIDGET_TITLE,
  DropdownToggleType.PAGE_TITLE,
  DropdownToggleType.ICON_ONLY
]);

type DropdownToggleProps<T extends string> = Pick<
  DropdownSharedProps<T>,
  | 'type'
  | 'className'
  | 'label'
  | 'ariaLabel'
  | 'leadingIcon'
  | 'size'
  | 'disabled'
  | 'placeholder'
  | 'overlay'
  | 'hideCaret'
  | 'clearable'
  | 'onClear'
> & {
  overlayPlacement?: PlacementType;
  readOnly?: boolean;
  value?: T;
  valueContent?: ReactNode; // Custom content rendered in place of the value/placeholder (e.g. multi-select chips). Opt-in; falls back to the value label when absent.
  options: readonly DropdownOption<T>[];
  hasError?: boolean;
  onToggleClick: () => void;
  isOpen: boolean;
  // Forwarded to the underlying button so react-bootstrap's DropdownContext
  // can register the toggle element via setToggle. Without this, popper has no
  // reference and menuAlign='end' can't compute its right-aligned transform.
  buttonRef?: Ref<HTMLButtonElement>;
  // Opt-in ref on the value span (not the placeholder), for pairing with
  // useIsTruncated to detect ellipsis truncation.
  valueRef?: Ref<HTMLSpanElement>;
};

export const DropdownToggle = <T extends string>({
  type = DropdownToggleType.TEXT_INPUT,
  className,
  label,
  ariaLabel,
  value,
  valueContent,
  options,
  leadingIcon,
  size: sizeProp,
  disabled,
  placeholder,
  overlay,
  overlayPlacement = Placement.AUTO,
  hasError,
  hideCaret = false,
  readOnly = false,
  clearable = false,
  onClear,
  onToggleClick,
  isOpen,
  buttonRef,
  valueRef
}: DropdownToggleProps<T>): ReactElement => {
  const size = useToolbarSize(sizeProp);
  const selectedOption = useMemo(
    () =>
      options.find(option => (typeof option === 'string' ? option : option.value) === value) ||
      value,
    [value, options]
  );

  const isIconOnly = type === DropdownToggleType.ICON_ONLY;

  const getValueLabel = (): ReactElement => {
    if (!selectedOption) return <span className='dropdown-placeholder'>{placeholder || ''}</span>;
    const text =
      typeof selectedOption === 'string'
        ? selectedOption
        : selectedOption.label || selectedOption.value;
    return (
      <span className='dropdown-value' ref={valueRef}>
        {text}
      </span>
    );
  };

  const button = (
    <DropdownToggleShell
      appearance={isIconOnly ? undefined : typeAppearance[type]}
      size={size}
      disabled={disabled}
      ref={buttonRef}
      onClick={readOnly ? undefined : onToggleClick}
      aria-disabled={readOnly || undefined}
      aria-label={ariaLabel}
      tabIndex={readOnly ? -1 : undefined}
      aria-expanded={isOpen}
      className={cn(
        NAMED_TYPES.has(type) && type,
        isIconOnly && 'bpm-icon-btn',
        readOnly && 'dropdown-read-only',
        className
      )}
      hasError={hasError}
      leadingIcon={leadingIcon}
      label={label}
      valueSlot={isIconOnly ? undefined : (valueContent ?? getValueLabel())}
      indicatorsSlot={
        <>
          {clearable && value && onClear && (
            <MdClose
              className='dropdown-clear-icon'
              data-testid='dropdown-clear-icon'
              role='button'
              tabIndex={0}
              aria-label='Clear'
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  onClear();
                }
              }}
            />
          )}
          {!hideCaret && !isIconOnly && <CaretIcon className='caret-icon' />}
        </>
      }
    />
  );

  if (overlay) {
    return (
      <OverlayTrigger placement={overlayPlacement} overlay={overlay}>
        {button}
      </OverlayTrigger>
    );
  }

  return button;
};
